/**
 * Parses Amazon Flex block details from OCR text
 */

export interface ParsedBlock {
  date: string | null; // YYYY-MM-DD format
  basePay: number | null; // In cents
  shiftStart: string | null; // HH:MM format (24-hour)
  shiftEnd: string | null; // HH:MM format (24-hour)
  duration: number | null; // In hours (for validation)
  station: string | null; // Station name e.g. "Oklahoma City OK (VOK1)"
  confidence: "high" | "medium" | "low";
  rawText: string;
}

// Month name to number mapping
const MONTHS: Record<string, number> = {
  jan: 0, january: 0,
  feb: 1, february: 1,
  mar: 2, march: 2,
  apr: 3, april: 3,
  may: 4,
  jun: 5, june: 5,
  jul: 6, july: 6,
  aug: 7, august: 7,
  sep: 8, sept: 8, september: 8,
  oct: 9, october: 9,
  nov: 10, november: 10,
  dec: 11, december: 11,
};

// Regex patterns for Amazon Flex block format
const PATTERNS = {
  // Matches $72.00, $54.50, $126.00, $72 (no cents), $ 72.00 (with space)
  // Also handles OCR errors like S72.00 (S instead of $)
  pay: /[$S]\s*(\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?)/g,

  // Matches 3 hr, 3.5 hr, 4.0 hr, 4hr
  duration: /(\d+(?:\.\d+)?)\s*hr/gi,

  // Matches 5:00 AM - 8:00 AM, 10:30 PM - 2:00 AM
  // Handles various dash types: -, –, —
  // Also handles newlines between start and end time (schedule view)
  timeRange: /(\d{1,2}):(\d{2})\s*(AM|PM)\s*[-–—]\s*[\n\r]*\s*(\d{1,2}):(\d{2})\s*(AM|PM)/gi,

  // Matches "Fri, Nov 14", "Thu, Nov 13", "Mon, Dec 25", etc.
  dateWithDay: /(Mon|Tue|Wed|Thu|Fri|Sat|Sun),?\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\s+(\d{1,2})/gi,

  // Matches "Nov 14", "Dec 25" without day of week
  dateSimple: /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\s+(\d{1,2})/gi,

  // Matches "Friday, 12/19", "Monday, 1/5" (Schedule view format)
  dateSchedule: /(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday),?\s+(\d{1,2})\/(\d{1,2})/gi,

  // Matches "SATURDAY DECEMBER 27" (block detail view format - all caps)
  dateDetail: /(MONDAY|TUESDAY|WEDNESDAY|THURSDAY|FRIDAY|SATURDAY|SUNDAY)\s+(JANUARY|FEBRUARY|MARCH|APRIL|MAY|JUNE|JULY|AUGUST|SEPTEMBER|OCTOBER|NOVEMBER|DECEMBER)\s+(\d{1,2})/gi,

  // Matches separate start/end times (block detail view)
  // "Start" followed by time, "End by" followed by time
  separateTime: /(?:Start|End\s*by)\s*[\n\r]*\s*(\d{1,2}):(\d{2})\s*(AM|PM)/gi,

  // Matches station names like "Oklahoma City OK (VOK1)" or "Denver CO (DEN2)"
  // Format: City Name STATE (CODE) - optionally followed by type
  station: /([A-Z][a-zA-Z\s]+(?:OK|TX|CA|CO|FL|NY|GA|NC|AZ|WA|OR|NV|IL|PA|OH|MI|VA|TN|IN|MO|MD|WI|MN|SC|AL|LA|KY|CT|UT|IA|AR|MS|KS|NM|NE|WV|ID|HI|NH|ME|MT|RI|DE|SD|ND|AK|VT|WY|DC))\s*\([A-Z0-9]{3,5}\)/gi,
};

/**
 * Convert 12-hour time to 24-hour HH:MM format
 */
function to24Hour(hours: number, minutes: number, period: string): string {
  let h = hours;
  const p = period.toUpperCase();

  if (p === "AM") {
    if (h === 12) h = 0;
  } else if (p === "PM") {
    if (h !== 12) h += 12;
  }

  return `${h.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
}

/**
 * Parse dollar amount to cents
 */
function parseDollarsToCents(dollarString: string): number {
  // Remove commas and parse
  const cleaned = dollarString.replace(/,/g, "");
  const dollars = parseFloat(cleaned);
  return Math.round(dollars * 100);
}

/**
 * Parse date string like "Nov 14" to YYYY-MM-DD
 * Uses current year, adjusts if date would be in the future
 */
function parseDate(monthStr: string, day: number): string {
  const month = MONTHS[monthStr.toLowerCase()];
  if (month === undefined) return "";

  const now = new Date();
  let year = now.getFullYear();

  // Create date with current year
  const date = new Date(year, month, day);

  // If date is more than 30 days in the future, assume previous year
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

  if (date > thirtyDaysFromNow) {
    year--;
  }

  return `${year}-${(month + 1).toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
}

/**
 * Parse MM/DD format to YYYY-MM-DD
 * Uses current year, adjusts if date would be in the future
 */
function parseDateNumeric(month: number, day: number): string {
  const now = new Date();
  let year = now.getFullYear();

  // Create date with current year (month is 1-indexed from regex)
  const date = new Date(year, month - 1, day);

  // If date is more than 30 days in the future, assume previous year
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

  if (date > thirtyDaysFromNow) {
    year--;
  }

  return `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
}

/**
 * Parse OCR text and extract a single block's details (legacy support)
 */
export function parseBlockText(text: string): ParsedBlock {
  const blocks = parseMultipleBlocks(text);
  return blocks[0] || {
    date: null,
    basePay: null,
    shiftStart: null,
    shiftEnd: null,
    duration: null,
    station: null,
    confidence: "low",
    rawText: text,
  };
}

/**
 * Parse OCR text and extract multiple blocks from Earnings tab or Schedule view
 */
export function parseMultipleBlocks(text: string): ParsedBlock[] {
  const blocks: ParsedBlock[] = [];

  // Find all dates, times, pay amounts, and stations
  const dates: { match: string; parsedDate: string; index: number }[] = [];
  const times: { start: string; end: string; index: number }[] = [];
  const pays: { amount: number; index: number }[] = [];
  const stations: { name: string; index: number }[] = [];

  // Extract station names (e.g., "Oklahoma City OK (VOK1)")
  let stationMatch;
  const stationRegex = new RegExp(PATTERNS.station.source, "gi");
  while ((stationMatch = stationRegex.exec(text)) !== null) {
    stations.push({
      name: stationMatch[0].trim(),
      index: stationMatch.index,
    });
  }

  // Try detail view format first (e.g., "SATURDAY DECEMBER 27")
  let dateMatch;
  const detailRegex = new RegExp(PATTERNS.dateDetail.source, "gi");
  while ((dateMatch = detailRegex.exec(text)) !== null) {
    // Month name is in group 2 (full month name like DECEMBER)
    const monthName = dateMatch[2].toLowerCase();
    const day = parseInt(dateMatch[3], 10);
    dates.push({
      match: dateMatch[0],
      parsedDate: parseDate(monthName, day),
      index: dateMatch.index,
    });
  }

  // Try schedule format (e.g., "Friday, 12/19")
  if (dates.length === 0) {
    const scheduleRegex = new RegExp(PATTERNS.dateSchedule.source, "gi");
    while ((dateMatch = scheduleRegex.exec(text)) !== null) {
      const month = parseInt(dateMatch[2], 10);
      const day = parseInt(dateMatch[3], 10);
      dates.push({
        match: dateMatch[0],
        parsedDate: parseDateNumeric(month, day),
        index: dateMatch.index,
      });
    }
  }

  // Try dates with day of week (e.g., "Fri, Nov 14")
  if (dates.length === 0) {
    const dateRegex = new RegExp(PATTERNS.dateWithDay.source, "gi");
    while ((dateMatch = dateRegex.exec(text)) !== null) {
      dates.push({
        match: dateMatch[0],
        parsedDate: parseDate(dateMatch[2], parseInt(dateMatch[3], 10)),
        index: dateMatch.index,
      });
    }
  }

  // Try simple dates (e.g., "Nov 14")
  if (dates.length === 0) {
    const simpleDateRegex = new RegExp(PATTERNS.dateSimple.source, "gi");
    while ((dateMatch = simpleDateRegex.exec(text)) !== null) {
      dates.push({
        match: dateMatch[0],
        parsedDate: parseDate(dateMatch[1], parseInt(dateMatch[2], 10)),
        index: dateMatch.index,
      });
    }
  }

  // Extract time ranges (e.g., "5:00 AM - 8:00 AM")
  let timeMatch;
  const timeRegex = new RegExp(PATTERNS.timeRange.source, "gi");
  while ((timeMatch = timeRegex.exec(text)) !== null) {
    const startHour = parseInt(timeMatch[1], 10);
    const startMin = parseInt(timeMatch[2], 10);
    const startPeriod = timeMatch[3];
    const endHour = parseInt(timeMatch[4], 10);
    const endMin = parseInt(timeMatch[5], 10);
    const endPeriod = timeMatch[6];

    times.push({
      start: to24Hour(startHour, startMin, startPeriod),
      end: to24Hour(endHour, endMin, endPeriod),
      index: timeMatch.index,
    });
  }

  // If no time ranges found, try separate Start/End times (detail view)
  if (times.length === 0) {
    const separateTimes: { time: string; type: "start" | "end"; index: number }[] = [];
    const separateRegex = new RegExp(PATTERNS.separateTime.source, "gi");
    let sepMatch;
    while ((sepMatch = separateRegex.exec(text)) !== null) {
      const hour = parseInt(sepMatch[1], 10);
      const min = parseInt(sepMatch[2], 10);
      const period = sepMatch[3];
      const isStart = text.slice(Math.max(0, sepMatch.index - 10), sepMatch.index).toLowerCase().includes("start");
      separateTimes.push({
        time: to24Hour(hour, min, period),
        type: isStart ? "start" : "end",
        index: sepMatch.index,
      });
    }

    // Pair start and end times
    const startTime = separateTimes.find(t => t.type === "start");
    const endTime = separateTimes.find(t => t.type === "end");
    if (startTime && endTime) {
      times.push({
        start: startTime.time,
        end: endTime.time,
        index: startTime.index,
      });
    }
  }

  // Extract pay amounts
  let payMatch;
  const payRegex = new RegExp(PATTERNS.pay.source, "gi");
  while ((payMatch = payRegex.exec(text)) !== null) {
    pays.push({
      amount: parseDollarsToCents(payMatch[1]),
      index: payMatch.index,
    });
  }

  // Schedule view: One date at top, multiple time/pay pairs below
  // If we have 1 date but multiple times, pair each time with closest pay and station
  if (dates.length === 1 && times.length > 1) {
    const sharedDate = dates[0].parsedDate;
    const usedPayIndicesSchedule = new Set<number>();
    const usedStationIndicesSchedule = new Set<number>();

    // Sort times by index
    const sortedTimes = [...times].sort((a, b) => a.index - b.index);

    for (let i = 0; i < sortedTimes.length; i++) {
      const time = sortedTimes[i];
      const prevTimeIndex = sortedTimes[i - 1]?.index ?? 0;
      const nextTimeIndex = sortedTimes[i + 1]?.index ?? text.length;

      // Find station closest BEFORE this time (station appears above time in the card)
      let closestStation: { name: string; index: number } | undefined;
      let closestStationDistance = Infinity;

      for (const s of stations) {
        if (usedStationIndicesSchedule.has(s.index)) continue;
        // Station should be before this time but after previous time
        if (s.index < time.index && s.index > prevTimeIndex) {
          const distance = time.index - s.index;
          if (distance < closestStationDistance) {
            closestStationDistance = distance;
            closestStation = s;
          }
        }
      }

      if (closestStation) usedStationIndicesSchedule.add(closestStation.index);

      // Find pay closest to this time (between this time and next time)
      let closestPay: { amount: number; index: number } | undefined;
      let closestPayDistance = Infinity;

      for (const p of pays) {
        if (usedPayIndicesSchedule.has(p.index)) continue;
        // Pay should be after this time but before next time
        if (p.index > time.index && p.index < nextTimeIndex) {
          const distance = p.index - time.index;
          if (distance < closestPayDistance) {
            closestPayDistance = distance;
            closestPay = p;
          }
        }
      }

      // If no pay found after, look for closest unused pay overall
      if (!closestPay) {
        for (const p of pays) {
          if (usedPayIndicesSchedule.has(p.index)) continue;
          const distance = Math.abs(p.index - time.index);
          if (distance < closestPayDistance) {
            closestPayDistance = distance;
            closestPay = p;
          }
        }
      }

      if (closestPay) usedPayIndicesSchedule.add(closestPay.index);

      let matchCount = 0;
      if (sharedDate) matchCount++;
      if (time) matchCount++;
      if (closestPay) matchCount++;

      blocks.push({
        date: sharedDate || null,
        basePay: closestPay?.amount ?? null,
        shiftStart: time.start,
        shiftEnd: time.end,
        duration: null,
        station: closestStation?.name ?? null,
        confidence: matchCount >= 3 ? "high" : matchCount >= 2 ? "medium" : "low",
        rawText: text.slice(closestStation?.index ?? time.index, nextTimeIndex).trim(),
      });
    }

    return blocks;
  }

  // Standard case: Multiple dates, each with their own time/pay
  // OCR may read in columns (all dates first, then all pays), so use order-based matching
  // Sort all items by position and match by index order
  const sortedDates = [...dates].sort((a, b) => a.index - b.index);
  const sortedTimes = [...times].sort((a, b) => a.index - b.index);
  const sortedPays = [...pays].sort((a, b) => a.index - b.index);
  const sortedStations = [...stations].sort((a, b) => a.index - b.index);

  // Match by order: 1st date with 1st time and 1st pay, etc.
  for (let i = 0; i < sortedDates.length; i++) {
    const dateInfo = sortedDates[i];
    const time = sortedTimes[i];
    const pay = sortedPays[i];
    const station = sortedStations[i];

    const parsedDate = dateInfo.parsedDate;

    let matchCount = 0;
    if (parsedDate) matchCount++;
    if (time) matchCount++;
    if (pay) matchCount++;

    let confidence: "high" | "medium" | "low" = "low";
    if (matchCount >= 3) confidence = "high";
    else if (matchCount >= 2) confidence = "medium";

    blocks.push({
      date: parsedDate || null,
      basePay: pay?.amount ?? null,
      shiftStart: time?.start ?? null,
      shiftEnd: time?.end ?? null,
      duration: null,
      station: station?.name ?? null,
      confidence,
      rawText: dateInfo.match,
    });
  }

  // If no dates found but we have time and pay, create a single block
  if (blocks.length === 0 && (times.length > 0 || pays.length > 0)) {
    let matchCount = 0;
    if (times.length > 0) matchCount++;
    if (pays.length > 0) matchCount++;

    blocks.push({
      date: null,
      basePay: pays[0]?.amount ?? null,
      shiftStart: times[0]?.start ?? null,
      shiftEnd: times[0]?.end ?? null,
      duration: null,
      station: stations[0]?.name ?? null,
      confidence: matchCount >= 2 ? "medium" : "low",
      rawText: text,
    });
  }

  return blocks;
}

/**
 * Validate parsed block data is reasonable
 */
export function validateParsedBlock(block: ParsedBlock): string[] {
  const errors: string[] = [];

  if (block.basePay !== null) {
    // Pay should be between $10 and $500
    if (block.basePay < 1000 || block.basePay > 50000) {
      errors.push("Pay amount seems unusual (expected $10-$500)");
    }
  }

  if (block.duration !== null) {
    // Duration should be between 1 and 8 hours
    if (block.duration < 1 || block.duration > 8) {
      errors.push("Duration seems unusual (expected 1-8 hours)");
    }
  }

  return errors;
}
